import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, StatusBadge, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function AdminCourses() {
  const [status, setStatus] = useState("submitted");
  const [courses, setCourses] = useState(null);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const reqId = useRef(0);

  const load = () => {
    const id = ++reqId.current;
    setCourses(null);
    api.get(`/admin/courses?status=${status}`)
      .then(({ data }) => { if (id === reqId.current) setCourses(data.courses); })
      .catch(() => { if (id === reqId.current) setCourses([]); });
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/courses/${action.course.id}/review`, { action: action.action, reason });
      toast.success(`Course ${action.action}d`);
      setAction(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-courses-page">
      <div className="flex gap-2 flex-wrap mb-6">
        {["submitted", "published", "correction_required", "rejected", "on_hold"].map((f) => (
          <button
            key={f}
            onClick={() => setStatus(f)}
            data-testid={`filter-${f}`}
            className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors capitalize ${
              status === f ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border hover:border-[#2563EB]"
            }`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {!courses ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : courses.length === 0 ? (
        <EmptyState title="No courses in this state" description="Courses submitted by institutes appear here for review." testId="admin-courses-empty" />
      ) : (
        <div className="space-y-4">
          {courses.map((c) => (
            <div key={c.id} className="bg-white border border-border/60 rounded-xl p-6" data-testid={`admin-course-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-heading font-semibold text-lg text-[#0B1B3D]">{c.title}</p>
                    <StatusBadge status={c.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {c.institute_name} · {c.category} · {c.mode} · {c.duration} · {inr(c.price - (c.discount || 0))} · {fmtDate(c.created_at)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2 max-w-3xl">{c.description}</p>
                </div>
              </div>
              {c.status === "submitted" && (
                <div className="flex gap-2 mt-5">
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ course: c, action: "approve" })} data-testid={`approve-course-${c.id}`}>
                    Approve & Publish
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAction({ course: c, action: "correction" })} data-testid={`correction-course-${c.id}`}>
                    Request Correction
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => setAction({ course: c, action: "reject" })} data-testid={`reject-course-${c.id}`}>
                    Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!action} onOpenChange={() => setAction(null)}>
        <DialogContent aria-describedby={undefined} data-testid="course-review-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{action?.action} — {action?.course.title}</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Note / reason</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" data-testid="course-reason-input" />
          </div>
          <Button onClick={review} disabled={busy} className="w-full rounded-full bg-[#0B1B3D]" data-testid="course-review-confirm">
            {busy ? "Processing…" : "Confirm"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
